/**
 * Step1CustomerInfo Component
 *
 * Collects customer identification and property address
 * - Customer first and last name
 * - Property address using Google Places autocomplete
 * - Auto-populates street, city, state, and zip
 */

import AddressAutocomplete from '../AddressAutocomplete';

export default function Step1CustomerInfo({ state, setField, errors }) {
  return (
    <div className="card">
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: '#2c3e50',
          marginBottom: '8px'
        }}>
          Step 1: Customer Information
        </h2>
        <p style={{ color: '#64748b', fontSize: '15px', lineHeight: '1.6' }}>
          Enter the customer's name and property address to begin the assessment.
        </p>
      </div>

      {/* Customer Name */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {/* First Name */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              First Name <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              className="input"
              value={state.customerFirstName || ''}
              onChange={(e) => setField('customerFirstName', e.target.value)}
              placeholder="John"
              required
              autoFocus
            />
            {errors.customerFirstName && (
              <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '5px' }}>
                {errors.customerFirstName}
              </div>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Last Name <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              className="input"
              value={state.customerLastName || ''}
              onChange={(e) => setField('customerLastName', e.target.value)}
              placeholder="Smith"
              required
            />
            {errors.customerLastName && (
              <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '5px' }}>
                {errors.customerLastName}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Property Address Section */}
      <div style={{
        padding: '20px',
        background: '#f8fafc',
        borderRadius: '8px',
        border: '2px solid #e0e6ed',
        marginBottom: '16px'
      }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#2c3e50',
          marginBottom: '16px'
        }}>
          📍 Property Address
        </h3>

        {/* Street Address (with Google Autocomplete) */}
        <div style={{ marginBottom: '20px' }}>
          <AddressAutocomplete
            street={state.propertyStreet || ''}
            city={state.propertyCity || ''}
            state={state.propertyState || ''}
            zip={state.propertyZip || ''}
            onStreetChange={(value) => setField('propertyStreet', value)}
            onCityChange={(value) => setField('propertyCity', value)}
            onCountyChange={(value) => setField('propertyCounty', value)}
            onStateChange={(value) => setField('propertyState', value)}
            onZipChange={(value) => setField('propertyZip', value)}
            onAddressSelect={(address) => {
              // Optional: Could trigger additional logic when address is selected
              console.log('Address selected:', address);
            }}
          />
          {errors.propertyStreet && (
            <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '5px' }}>
              {errors.propertyStreet}
            </div>
          )}
        </div>

        {/* City, State, Zip (auto-filled from Google) */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '16px' }}>
          {/* City */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              City <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              className="input"
              value={state.propertyCity || ''}
              onChange={(e) => setField('propertyCity', e.target.value)}
              placeholder="Raleigh"
              required
            />
            {errors.propertyCity && (
              <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '5px' }}>
                {errors.propertyCity}
              </div>
            )}
          </div>

          {/* State */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              State <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              className="input"
              value={state.propertyState || ''}
              onChange={(e) => setField('propertyState', e.target.value)}
              placeholder="NC"
              required
              maxLength={2}
              style={{ textTransform: 'uppercase' }}
            />
            {errors.propertyState && (
              <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '5px' }}>
                {errors.propertyState}
              </div>
            )}
          </div>

          {/* Zip */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Zip <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              className="input"
              value={state.propertyZip || ''}
              onChange={(e) => setField('propertyZip', e.target.value)}
              placeholder="27601"
              required
              maxLength={5}
            />
            {errors.propertyZip && (
              <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '5px' }}>
                {errors.propertyZip}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Help text */}
      <div style={{
        padding: '12px',
        background: '#f0f9ff',
        borderRadius: '6px',
        fontSize: '13px',
        color: '#0c4a6e',
        lineHeight: '1.6'
      }}>
        💡 <strong>Tip:</strong> Use the address autocomplete to ensure accurate location data.
        This helps us suggest the correct utility providers and rebates in the next step.
      </div>
    </div>
  );
}
